import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabaseClient';
import { tasksService, type Task } from '../../../services/projectManagement/tasksService';

interface TeamMember {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
}

interface WorkloadData {
  member: TeamMember;
  tasks: Task[];
  totalEstimatedHours: number;
  totalActualHours: number;
  tasksByStatus: Record<string, number>;
  tasksByPriority: Record<string, number>;
}

interface TeamWorkloadViewProps {
  organizationId: string;
  onTaskClick: (taskId: string) => void;
}

const TeamWorkloadView: React.FC<TeamWorkloadViewProps> = ({ organizationId, onTaskClick }) => {
  const [loading, setLoading] = useState(true);
  const [workloadData, setWorkloadData] = useState<WorkloadData[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<'name' | 'tasks' | 'hours'>('tasks');

  const fetchTeamWorkload = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch organization members
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select(`
          user_id,
          users:user_id (
            id,
            email,
            raw_user_meta_data
          )
        `)
        .eq('organization_id', organizationId);

      if (membersError) throw membersError;

      // Fetch all tasks
      const { data: tasks, error: tasksError } = await tasksService.list({
        organization_id: organizationId,
      });

      if (tasksError) throw tasksError;

      // Build workload data for each member
      const workloadMap: WorkloadData[] = [];

      members?.forEach((member: any) => {
        const user = member.users;
        if (!user) return;

        const memberTasks = (tasks || []).filter(
          (task) => task.assignee_id === user.id
        );

        const totalEstimatedHours = memberTasks.reduce(
          (sum, task) => sum + (task.estimated_time || 0),
          0
        );

        const totalActualHours = memberTasks.reduce(
          (sum, task) => sum + (task.actual_time || 0),
          0
        );

        const tasksByStatus: Record<string, number> = {};
        const tasksByPriority: Record<string, number> = {};

        memberTasks.forEach((task) => {
          tasksByStatus[task.status] = (tasksByStatus[task.status] || 0) + 1;
          tasksByPriority[task.priority] = (tasksByPriority[task.priority] || 0) + 1;
        });

        workloadMap.push({
          member: {
            id: user.id,
            email: user.email,
            full_name: user.raw_user_meta_data?.full_name,
            avatar_url: user.raw_user_meta_data?.avatar_url,
          },
          tasks: memberTasks,
          totalEstimatedHours,
          totalActualHours,
          tasksByStatus,
          tasksByPriority,
        });
      });

      // Sort workload data
      workloadMap.sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return (a.member.full_name || a.member.email).localeCompare(
              b.member.full_name || b.member.email
            );
          case 'tasks':
            return b.tasks.length - a.tasks.length;
          case 'hours':
            return b.totalEstimatedHours - a.totalEstimatedHours;
          default:
            return 0;
        }
      });

      setWorkloadData(workloadMap);
    } catch (error: any) {
      console.error('Error fetching team workload:', error);
      toast.error('Failed to load team workload');
    } finally {
      setLoading(false);
    }
  }, [organizationId, sortBy]);

  useEffect(() => {
    void fetchTeamWorkload();
  }, [fetchTeamWorkload]);

  const getWorkloadColor = (hours: number) => {
    if (hours >= 40) return 'text-red-600 bg-red-50';
    if (hours >= 30) return 'text-orange-600 bg-orange-50';
    if (hours >= 20) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading team workload...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team Workload</h2>
          <p className="text-sm text-gray-600 mt-1">
            {workloadData.length} team members
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="name">Sort by Name</option>
            <option value="tasks">Sort by Task Count</option>
            <option value="hours">Sort by Hours</option>
          </select>

          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 text-sm font-medium ${
                viewMode === 'cards'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📊 Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📋 Table
            </button>
          </div>
        </div>
      </div>

      {/* No team members */}
      {workloadData.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-lg font-semibold text-gray-900">No Team Members</h3>
          <p className="mt-2 text-sm text-gray-600">
            Invite team members to see their workload
          </p>
        </div>
      ) : viewMode === 'cards' ? (
        /* Card View */
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {workloadData.map((data) => (
            <div
              key={data.member.id}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Member Info */}
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-600">
                  {(data.member.full_name || data.member.email).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {data.member.full_name || 'Team Member'}
                  </h3>
                  <p className="text-sm text-gray-600 truncate">{data.member.email}</p>
                </div>
              </div>

              {/* Workload Stats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Tasks:</span>
                  <span className="text-lg font-bold text-gray-900">{data.tasks.length}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Estimated Hours:</span>
                  <span className={`rounded-full px-3 py-1 text-sm font-bold ${getWorkloadColor(data.totalEstimatedHours)}`}>
                    {data.totalEstimatedHours}h
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Actual Hours:</span>
                  <span className="text-sm font-medium text-gray-900">{data.totalActualHours}h</span>
                </div>
              </div>

              {/* Tasks by Status */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.tasksByStatus).map(([status, count]) => (
                    <span
                      key={status}
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(status)}`}
                    >
                      {status}: {count}
                    </span>
                  ))}
                </div>
              </div>

              {/* Recent Tasks */}
              {data.tasks.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Recent Tasks:</p>
                  <div className="space-y-1">
                    {data.tasks.slice(0, 3).map((task) => (
                      <button
                        key={task.id}
                        onClick={() => onTaskClick(task.id)}
                        className="w-full text-left text-xs text-gray-600 hover:text-blue-600 truncate"
                      >
                        • {task.title}
                      </button>
                    ))}
                    {data.tasks.length > 3 && (
                      <p className="text-xs text-gray-500">+{data.tasks.length - 3} more</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Member
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Tasks
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Hours
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actual Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status Distribution
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {workloadData.map((data) => (
                <tr key={data.member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                        {(data.member.full_name || data.member.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {data.member.full_name || 'Team Member'}
                        </div>
                        <div className="text-sm text-gray-500">{data.member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-sm font-semibold text-gray-900">{data.tasks.length}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`rounded-full px-3 py-1 text-sm font-bold ${getWorkloadColor(data.totalEstimatedHours)}`}>
                      {data.totalEstimatedHours}h
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-sm text-gray-900">{data.totalActualHours}h</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(data.tasksByStatus).map(([status, count]) => (
                        <span
                          key={status}
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(status)}`}
                        >
                          {status}: {count}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Capacity Legend */}
      <div className="flex items-center justify-center gap-6 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        <span className="text-gray-700 font-medium">Capacity:</span>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-green-600" />
          <span className="text-gray-700">&lt; 20h (Light)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-yellow-600" />
          <span className="text-gray-700">20-30h (Moderate)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-orange-600" />
          <span className="text-gray-700">30-40h (High)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-red-600" />
          <span className="text-gray-700">40h+ (Overloaded)</span>
        </div>
      </div>
    </div>
  );
};

export default TeamWorkloadView;
